import { notify } from '@/lib/toast';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Car, Clock, ChefHat, CheckCircle2, Receipt, Wifi, WifiOff,
  ClipboardList, Plus, User, Bell, UserCheck,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmptyState } from '@/components/ui/empty-state';
import { useLocationStore } from '@/stores/location.store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuthStore } from '@/stores/auth.store';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { getSocket } from '@/lib/socket';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { DiningOrder } from '@/types/restaurant';
import { diningItemsNet } from '@/lib/dining-order-total';
import { Dropdown } from 'primereact/dropdown';

// ── Helpers ───────────────────────────────────────────────────────────────────

function elapsedMinutes(createdAt: string): number {
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
}

function elapsedLabel(mins: number): string {
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function elapsedColor(mins: number): string {
  if (mins < 5) return 'text-success-text';
  if (mins < 12) return 'text-warning-text';
  return 'text-destructive-text';
}

function minutesBetween(fromIso: string, toIso?: string | null): number {
  const end = toIso ? new Date(toIso).getTime() : Date.now();
  return Math.max(0, Math.floor((end - new Date(fromIso).getTime()) / 60000));
}

/** Short descending tone used for both the READY bell and the (higher) car-arrived chime. */
function playTone(startHz: number, endHz: number): void {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.setValueAtTime(startHz, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(endHz, ctx.currentTime + 0.3);
  gain.gain.setValueAtTime(0.3, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
  osc.start();
  osc.stop(ctx.currentTime + 0.6);
}

interface StaffUser { id: string; firstName: string; lastName: string }

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Waiting',
  PREPARING: 'Preparing',
  READY: 'Ready for Delivery',
  SERVED: 'Delivered',
  BILLED: 'Billed',
};

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-secondary text-foreground',
  PREPARING: 'bg-warning text-warning-foreground',
  READY: 'bg-success-solid text-success-foreground',
  SERVED: 'bg-info-text text-info-foreground',
  BILLED: 'bg-secondary text-muted-foreground',
};

type DiningOrderWithTotal = DiningOrder & { totalAmount?: number | string | null };

function orderTotal(order: DiningOrderWithTotal): number {
  return order.totalAmount != null
    ? Number(order.totalAmount)
    : diningItemsNet(order.items); // POS-I120: net of line discounts, as the server bills
}

// ── Card ──────────────────────────────────────────────────────────────────────

interface TakeawayCardProps {
  order: DiningOrder;
  onDeliver: (id: string) => void;
  onBill: (id: string) => void;
  isDelivering: boolean;
  isReady: boolean;
  canViewCar: boolean;
  canManageCar: boolean;
  staff: StaffUser[];
  onCarArrived: (id: string) => void;
  onClaimRunner: (id: string, runnerId: string) => void;
  isCarBusy: boolean;
}

function TakeawayCard({
  order, onDeliver, onBill, isDelivering, isReady,
  canViewCar, canManageCar, staff, onCarArrived, onClaimRunner, isCarBusy,
}: TakeawayCardProps) {
  const { t } = useTranslation('restaurant');
  const mins = elapsedMinutes(order.createdAt);
  const activeItems = order.items.filter((i) => i.kitchenStatus !== 'CANCELLED');
  const readyCount = activeItems.filter((i) => i.kitchenStatus === 'READY' || i.kitchenStatus === 'SERVED').length;
  const carHere = canViewCar && !!order.carArrivedAt && !order.handedOverAt;
  const handedOver = canViewCar && !!order.carArrivedAt && !!order.handedOverAt;
  const showVehicle = canViewCar && (order.vehicleDescription || order.vehiclePlate);
  const runnerName = order.runner ? `${order.runner.firstName} ${order.runner.lastName}`.trim() : '';

  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-3 transition-all ${
        carHere
          ? 'border-primary bg-primary/10 ring-2 ring-primary shadow-lg'
          : isReady
            ? 'border-success bg-success/40 shadow-lg shadow-success/30 animate-pulse-border'
            : 'border-border bg-card'
      }`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-extrabold text-lg text-foreground">#{order.orderNo}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${STATUS_COLORS[order.status] ?? ''}`}>
          {STATUS_LABEL[order.status] ?? order.status}
        </span>
      </div>

      {carHere && (
        <StatusBadge
          status="CAR_HERE"
          tone="primary"
          solid
          icon={<Bell className="h-4 w-4" />}
          label={t('carHere')}
          className="w-fit px-3 py-1 text-sm font-bold"
        />
      )}

      {/* Car slot — hero display */}
      {order.carSlot ? (
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 ${isReady ? 'bg-success/60' : 'bg-card'}`}>
          <Car className={`h-5 w-5 flex-shrink-0 ${isReady ? 'text-success-text' : 'text-muted-foreground'}`} />
          <span className={`text-xl font-bold tracking-wide ${isReady ? 'text-success-text' : 'text-foreground'}`}>
            {order.carSlot}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-card">
          <Car className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground italic">No slot assigned</span>
        </div>
      )}

      {showVehicle && (
        <p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-foreground">
          <Car className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          {order.vehicleDescription && <span>{order.vehicleDescription}</span>}
          {order.vehiclePlate && (
            <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs tracking-wider text-foreground">
              {order.vehiclePlate}
            </span>
          )}
        </p>
      )}

      {/* Items summary */}
      <div className="space-y-1">
        {activeItems.slice(0, 4).map((item) => (
          <div key={item.id} className="flex items-center justify-between text-sm gap-2">
            <span className="text-foreground truncate">
              <span className="font-semibold text-foreground">×{item.quantity}</span> {item.product?.name ?? 'Item'}
            </span>
            <span className={`text-xs font-semibold rounded px-1.5 py-0.5 ${
              item.kitchenStatus === 'READY' ? 'bg-success text-success-foreground' :
              item.kitchenStatus === 'SERVED' ? 'bg-info-text text-info-foreground' :
              item.kitchenStatus === 'PREPARING' ? 'bg-warning text-warning-foreground' :
              'bg-secondary text-muted-foreground'
            }`}>
              {item.kitchenStatus}
            </span>
          </div>
        ))}
        {activeItems.length > 4 && (
          <p className="text-xs text-muted-foreground">+{activeItems.length - 4} more items</p>
        )}
      </div>

      {/* Progress bar */}
      {activeItems.length > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{readyCount}/{activeItems.length} ready</span>
            <span>{formatCurrency(orderTotal(order))}</span>
          </div>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${isReady ? 'bg-success' : 'bg-warning'}`}
              style={{ width: `${activeItems.length > 0 ? (readyCount / activeItems.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Elapsed */}
      <div className={`flex items-center gap-1.5 text-base font-semibold ${elapsedColor(mins)}`}>
        <Clock className="h-4 w-4" />
        {elapsedLabel(mins)}
      </div>

      {carHere && order.carArrivedAt && (
        <p className="text-sm font-semibold text-primary-text">
          {t('carWaitingFor', { time: elapsedLabel(minutesBetween(order.carArrivedAt)) })}
        </p>
      )}
      {canViewCar && order.readyAt && order.carArrivedAt && (
        <p className="text-sm text-muted-foreground">
          {t('arrivedAfterReady', { time: elapsedLabel(minutesBetween(order.readyAt, order.carArrivedAt)) })}
        </p>
      )}
      {handedOver && order.carArrivedAt && order.handedOverAt && (
        <p className="text-sm text-muted-foreground">
          {t('handedOverAfter', { time: elapsedLabel(minutesBetween(order.carArrivedAt, order.handedOverAt)) })}
        </p>
      )}

      {/* Runner */}
      {canViewCar && !canManageCar && runnerName && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <UserCheck className="h-4 w-4 flex-shrink-0" />
          {t('runnerLabel', { name: runnerName })}
        </p>
      )}
      {canManageCar && order.status !== 'SERVED' && (
        <Dropdown
          className="w-full"
          value={order.runnerId ?? null}
          onChange={(e) => { if (e.value) onClaimRunner(order.id, e.value as string); }}
          options={staff.map((u) => ({ label: `${u.firstName} ${u.lastName}`.trim(), value: u.id }))}
          optionLabel="label"
          optionValue="value"
          placeholder={t('selectRunnerPlaceholder')}
          emptyMessage={t('noStaffFound')}
          filter
          disabled={isCarBusy}
          aria-label={t('runner')}
        />
      )}

      {/* Customer */}
      {order.customer && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground truncate"><User className="h-4 w-4 flex-shrink-0" />{order.customer.name}</p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        {canManageCar && !order.carArrivedAt && order.status !== 'SERVED' && (
          <Button
            variant="outline"
            className="flex-1 min-w-[8.5rem] h-12 text-base border-primary bg-card hover:bg-secondary text-primary-text font-semibold"
            onClick={() => onCarArrived(order.id)}
            disabled={isCarBusy}
          >
            <Bell className="h-4 w-4 me-1.5" />
            {t('carArrived')}
          </Button>
        )}
        {isReady && (
          <Button
            className="flex-1 min-w-[8.5rem] h-12 text-base bg-success-solid hover:bg-success/90 text-success-foreground font-semibold"
            onClick={() => onDeliver(order.id)}
            disabled={isDelivering}
          >
            <CheckCircle2 className="h-4 w-4 me-1.5" />
            {isDelivering ? 'Delivering…' : 'Mark Delivered'}
          </Button>
        )}
        <Button
          variant="outline"
          aria-label={t('billOrder')}
          className={`${isReady ? '' : 'flex-1 min-w-[5rem]'} border-border text-muted-foreground hover:bg-card`}
          onClick={() => onBill(order.id)}
          size="lg"
        >
          <Receipt className="h-4 w-4 me-1" />
          {isReady ? '' : 'Bill'}
        </Button>
      </div>
    </div>
  );
}

const emptyTakeawayForm = {
  guestCount: 1,
  notes: '',
  carSlot: '',
  vehicleDescription: '',
  vehiclePlate: '',
  locationId: '',
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export function TakeawayQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const { selectedLocationId } = useLocationStore();
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');
  const canManageCar = hasPermission('car-takeaway:manage') || hasPermission('car-takeaway:*');
  const canViewCar = canManageCar || hasPermission('car-takeaway:read');

  const [socketConnected, setSocketConnected] = useState(false);
  const [deliverConfirmId, setDeliverConfirmId] = useState<string | null>(null);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyTakeawayForm);
  const bellRef = useRef<boolean>(false);

  // Track socket
  useEffect(() => {
    const update = () => setSocketConnected(getSocket()?.connected ?? false);
    update();
    const interval = setInterval(update, 2000);
    const s = getSocket();
    s?.on('connect', update);
    s?.on('disconnect', update);
    return () => {
      clearInterval(interval);
      s?.off('connect', update);
      s?.off('disconnect', update);
    };
  }, []);

  const { data, isLoading } = useQuery<DiningOrder[]>({
    queryKey: ['takeaway-queue'],
    queryFn: async () => {
      const params = new URLSearchParams({
        type: 'TAKEAWAY',
        status: 'OPEN,PREPARING,READY,SERVED',
        limit: '50',
        page: '1',
      });
      const res = await api.get(`/dining-orders?${params}`);
      const list: DiningOrder[] = res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
      return list;
    },
    refetchInterval: 60000,
  });

  const orders = data ?? [];
  const readyOrders = orders.filter((o) => o.status === 'READY');
  const queueOrders = orders.filter((o) => ['OPEN', 'PREPARING'].includes(o.status));
  const servedOrders = orders.filter((o) => o.status === 'SERVED');

  // Play a sound when new READY orders appear
  const prevReadyCount = useRef(0);
  useEffect(() => {
    if (readyOrders.length > prevReadyCount.current && !bellRef.current) {
      bellRef.current = true;
      try {
        playTone(880, 440);
        setTimeout(() => { bellRef.current = false; }, 2000);
      } catch {
        bellRef.current = false;
      }
    }
    prevReadyCount.current = readyOrders.length;
  }, [readyOrders.length]);

  // Real-time refresh
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['takeaway-queue'] });
  }, [queryClient]);
  useSocketEvent('dining_order.created', refresh);
  useSocketEvent('dining_order.updated', refresh);
  useSocketEvent('dining_order.delivered', refresh);
  useSocketEvent('dining_order.billed', refresh);
  useSocketEvent('kitchen.item_updated', refresh);
  useSocketEvent<{ orderNo?: string }>('dining_order.car_arrived', (payload) => {
    refresh();
    if (!canViewCar) return;
    toast(t('carArrivedAlert', { orderNo: payload?.orderNo ?? '' }));
    try {
      playTone(1320, 990);
    } catch {
      // Audio autoplay can be blocked by the browser; the on-card "Car here" highlight still shows.
    }
  });

  const { data: staffUsers } = useQuery<StaffUser[]>({
    queryKey: ['staff-users'],
    queryFn: () => api.get('/users?limit=200').then((r) => {
      const list: StaffUser[] = r.data?.data ?? r.data ?? [];
      return list.map((u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName }));
    }),
    enabled: canManageCar,
  });

  const carArrivedMutation = useMutation({
    mutationFn: (orderId: string) => api.post(`/dining-orders/${orderId}/car-arrived`),
    onSuccess: () => {
      toast.success(t('carArrivedMarked'));
      queryClient.invalidateQueries({ queryKey: ['takeaway-queue'] });
    },
    onError: (err) => notify.error(err, t('carArrivedFailed')),
  });

  const claimRunnerMutation = useMutation({
    mutationFn: ({ orderId, runnerId }: { orderId: string; runnerId: string }) =>
      api.patch(`/dining-orders/${orderId}/claim-runner`, { runnerId }),
    onSuccess: () => {
      toast.success(t('runnerAssigned'));
      queryClient.invalidateQueries({ queryKey: ['takeaway-queue'] });
    },
    onError: (err) => notify.error(err, t('runnerAssignFailed')),
  });

  const markDeliveredMutation = useMutation({
    mutationFn: (orderId: string) => api.post(`/dining-orders/${orderId}/mark-delivered`),
    onSuccess: () => {
      toast.success('Order marked as delivered');
      queryClient.invalidateQueries({ queryKey: ['takeaway-queue'] });
      setDeliverConfirmId(null);
    },
    onError: (err) => notify.error(err, 'Failed to mark as delivered'),
  });

  const handleBill = (orderId: string) => navigate(`/restaurant/orders/${orderId}`);

  const { data: locations } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
    enabled: newOrderOpen,
  });

  const createOrderMutation = useMutation({
    mutationFn: async ({ order, car }: { order: Record<string, unknown>; car: { vehicleDescription?: string; vehiclePlate?: string } | null }) => {
      const res = await api.post('/dining-orders', order);
      if (car) {
        try {
          await api.patch(`/dining-orders/${res.data.id}/car-details`, car);
        } catch (err) {
          notify.error(err, t('carDetailsSaveFailed'));
        }
      }
      return res;
    },
    onSuccess: (res: { data: { id: string } }) => {
      toast.success(t('orderCreated'));
      queryClient.invalidateQueries({ queryKey: ['takeaway-queue'] });
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      setNewOrderOpen(false);
      setOrderForm(emptyTakeawayForm);
      navigate(`/restaurant/orders/${res.data.id}`);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err?.response?.data?.message ?? t('failedToCreateOrder')),
  });

  const openNewTakeaway = () => {
    setOrderForm({ ...emptyTakeawayForm, locationId: selectedLocationId ?? '' });
    setNewOrderOpen(true);
  };

  const handleCreateTakeaway = () => {
    const vehicleDescription = orderForm.vehicleDescription.trim();
    const vehiclePlate = orderForm.vehiclePlate.trim();
    createOrderMutation.mutate({
      order: {
        type: 'TAKEAWAY',
        guestCount: Number(orderForm.guestCount),
        notes: orderForm.notes || undefined,
        carSlot: orderForm.carSlot || undefined,
        locationId: orderForm.locationId || undefined,
      },
      car: canManageCar && (vehicleDescription || vehiclePlate)
        ? { vehicleDescription: vehicleDescription || undefined, vehiclePlate: vehiclePlate || undefined }
        : null,
    });
  };

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between px-4 sm:px-6 py-3 bg-card border-b border-border gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Car className="h-5 w-5 text-primary-text" />
          <div>
            <span className="text-base sm:text-lg font-bold">Takeaway Queue</span>
            <p className="text-xs text-muted-foreground">Drive-through order tracker</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <div className={`flex items-center gap-1.5 text-sm font-medium px-2 py-1 rounded-full ${socketConnected ? 'bg-success/50 text-success-text' : 'bg-destructive/50 text-destructive-text'}`}>
            {socketConnected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            {socketConnected ? 'Live' : 'Polling'}
          </div>
          {canWrite && (
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={openNewTakeaway}
            >
              <Plus className="h-4 w-4 me-1.5" />
              {t('newTakeawayOrder')}
            </Button>
          )}
          {canWrite && (
            <Button
              size="lg"
              variant="outline"
              className="border-border text-muted-foreground hover:bg-card"
              onClick={() => navigate('/restaurant/orders')}
            >
              <ClipboardList className="h-4 w-4 me-1.5" />
              {t('activeOrders')}
            </Button>
          )}
        </div>
      </header>

      <main className="flex-1 p-4 space-y-8">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-60 rounded-xl bg-card animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* READY section */}
            {readyOrders.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-2.5 w-2.5 rounded-full bg-success/30 animate-ping" />
                  <h2 className="text-lg font-bold text-success-text">
                    Ready for Delivery ({readyOrders.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {readyOrders.map((order) => (
                    <TakeawayCard
                      key={order.id}
                      order={order}
                      isReady
                      isDelivering={markDeliveredMutation.isPending && deliverConfirmId === order.id}
                      onDeliver={(id) => setDeliverConfirmId(id)}
                      onBill={handleBill}
                      canViewCar={canViewCar}
                      canManageCar={canManageCar}
                      staff={staffUsers ?? []}
                      onCarArrived={(id) => carArrivedMutation.mutate(id)}
                      onClaimRunner={(orderId, runnerId) => claimRunnerMutation.mutate({ orderId, runnerId })}
                      isCarBusy={carArrivedMutation.isPending || claimRunnerMutation.isPending}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* IN QUEUE section */}
            {queueOrders.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ChefHat className="h-4 w-4 text-warning-text" />
                  <h2 className="text-base font-semibold text-muted-foreground">
                    In Kitchen ({queueOrders.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {queueOrders.map((order) => (
                    <TakeawayCard
                      key={order.id}
                      order={order}
                      isReady={false}
                      isDelivering={false}
                      onDeliver={(id) => setDeliverConfirmId(id)}
                      onBill={handleBill}
                      canViewCar={canViewCar}
                      canManageCar={canManageCar}
                      staff={staffUsers ?? []}
                      onCarArrived={(id) => carArrivedMutation.mutate(id)}
                      onClaimRunner={(orderId, runnerId) => claimRunnerMutation.mutate({ orderId, runnerId })}
                      isCarBusy={carArrivedMutation.isPending || claimRunnerMutation.isPending}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* SERVED (recent) section */}
            {servedOrders.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-info-text" />
                  <h2 className="text-base font-semibold text-muted-foreground">
                    Delivered — Awaiting Payment ({servedOrders.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {servedOrders.map((order) => (
                    <TakeawayCard
                      key={order.id}
                      order={order}
                      isReady={false}
                      isDelivering={false}
                      onDeliver={() => {}}
                      onBill={handleBill}
                      canViewCar={canViewCar}
                      canManageCar={canManageCar}
                      staff={staffUsers ?? []}
                      onCarArrived={(id) => carArrivedMutation.mutate(id)}
                      onClaimRunner={(orderId, runnerId) => claimRunnerMutation.mutate({ orderId, runnerId })}
                      isCarBusy={carArrivedMutation.isPending || claimRunnerMutation.isPending}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Empty */}
            {orders.length === 0 && (
              <EmptyState
                className="min-h-[60vh]"
                icon={Car}
                title="No active takeaway orders"
                description={canWrite ? undefined : t('activeOrders')}
                action={
                  canWrite ? (
                    <Button
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                      onClick={openNewTakeaway}
                    >
                      <Plus className="h-4 w-4 me-1.5" />
                      {t('newTakeawayOrder')}
                    </Button>
                  ) : undefined
                }
              />
            )}
          </>
        )}
      </main>

      <Dialog
        visible={newOrderOpen}
        onHide={() => { setNewOrderOpen(false); setOrderForm(emptyTakeawayForm); }}
        header={t('newTakeawayOrder')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('carSlot')}</Label>
            <Input
              placeholder={t('carSlotPlaceholder')}
              value={orderForm.carSlot}
              onChange={(e) => setOrderForm({ ...orderForm, carSlot: e.target.value })}
            />
          </div>
          {canManageCar && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="takeaway-vehicle-description" className="mb-1.5 block">{t('vehicleDescription')}</Label>
                <Input
                  id="takeaway-vehicle-description"
                  placeholder={t('vehicleDescriptionPlaceholder')}
                  value={orderForm.vehicleDescription}
                  onChange={(e) => setOrderForm({ ...orderForm, vehicleDescription: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="takeaway-vehicle-plate" className="mb-1.5 block">{t('vehiclePlate')}</Label>
                <Input
                  id="takeaway-vehicle-plate"
                  placeholder={t('vehiclePlatePlaceholder')}
                  value={orderForm.vehiclePlate}
                  onChange={(e) => setOrderForm({ ...orderForm, vehiclePlate: e.target.value })}
                />
              </div>
            </div>
          )}
          {locations && locations.length > 1 && (
            <div>
              <Label className="mb-1.5 block">{t('location')}</Label>
              <Dropdown
                className="w-full"
                value={orderForm.locationId}
                onChange={(e) => setOrderForm({ ...orderForm, locationId: e.value })}
                options={[
                  { label: t('noSpecificLocation'), value: '' },
                  ...locations.map((l) => ({ label: l.name, value: l.id })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('noSpecificLocation')}
              />
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">{t('guestCount')}</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={orderForm.guestCount}
              onChange={(e) => setOrderForm({ ...orderForm, guestCount: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Input
              placeholder={t('optionalOrderNotes')}
              value={orderForm.notes}
              onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setNewOrderOpen(false); setOrderForm(emptyTakeawayForm); }}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleCreateTakeaway}
              disabled={createOrderMutation.isPending}
            >
              {createOrderMutation.isPending ? t('creating') : t('createOrder')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Confirm delivery dialog */}
      <ConfirmDialog
        open={!!deliverConfirmId}
        onCancel={() => setDeliverConfirmId(null)}
        onConfirm={() => {
          if (deliverConfirmId) markDeliveredMutation.mutate(deliverConfirmId);
        }}
        title="Mark as Delivered"
        message="Confirm that the order has been taken to the customer's car. This will mark all items as served."
        confirmLabel="Delivered"
        variant="warning"
        loading={markDeliveredMutation.isPending}
      />
    </div>
  );
}
